<template>
  <cms-custom-component
    :theme="themeOptions"
    :content="content"
  >
    <template #input="{ customName, link, inLoop, loopName, loopIndex }">
      <a
        v-if="link && getCustomValue(inLoop, loopName, loopIndex, 'input', customName, 'href')"
        :href="getCustomValue(inLoop, loopName, loopIndex, 'input', customName, 'href')"
        :target="getCustomValue(inLoop, loopName, loopIndex, 'input', customName, 'target')"
        >{{ getCustomValue(inLoop, loopName, loopIndex, 'input', customName, 'text') || '' }}</a
      >
      <template v-else>{{ getCustomValue(inLoop, loopName, loopIndex, 'input', customName, 'text') || '' }}</template>
    </template>
    <template #textarea="{ customName, link, inLoop, loopName, loopIndex }">
      <a
        v-if="link && getCustomValue(inLoop, loopName, loopIndex, 'textarea', customName, 'href')"
        :href="getCustomValue(inLoop, loopName, loopIndex, 'textarea', customName, 'href')"
        :target="getCustomValue(inLoop, loopName, loopIndex, 'textarea', customName, 'target')"
        >{{ getCustomValue(inLoop, loopName, loopIndex, 'textarea', customName, 'text') }}</a
      >
      <template v-else>{{ getCustomValue(inLoop, loopName, loopIndex, 'textarea', customName, 'text') }}</template>
    </template>
    <template #richtextarea="{ customName, inLoop, loopName, loopIndex }">
      <span v-html="getCustomValue(inLoop, loopName, loopIndex, 'rich_textarea', customName, 'text')" />
    </template>
    <template #image="{ customName, link, aspectRatio, inLoop, loopName, loopIndex }">
      <a
        v-if="link && getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'href')"
        :href="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'href')"
        :target="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'target')"
      >
        <img
          :src="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'url')"
          :alt="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'alt_text')"
          :style="{ aspectRatio }"
        />
      </a>
      <img
        v-else
        :src="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'url')"
        :alt="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'alt_text')"
        :style="{ aspectRatio }"
      />
    </template>
    <template #icon="{ customName, link, inLoop, loopName, loopIndex }">
      <a
        v-if="link && getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'href')"
        :href="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'href')"
        :target="getCustomValue(inLoop, loopName, loopIndex, 'image', customName, 'target')"
      >
        <span v-html="getCustomValue(inLoop, loopName, loopIndex, 'icon', customName, 'icon')" />
      </a>
      <span
        v-else
        v-html="getCustomValue(inLoop, loopName, loopIndex, 'icon', customName, 'icon')"
      />
    </template>
  </cms-custom-component>
</template>

<script setup>
import { computed } from 'vue';
import { useMainStore } from '../../../../stores/mainStore.js';
import { useTranslationStore } from '../../../../stores/translationStore.js';
import get from 'lodash/get';

const props = defineProps({
  containerId: {
    type: String,
    default: null,
  },
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
});

const mainStore = useMainStore();
const translationStore = useTranslationStore();

const theme = computed(() => mainStore.theme);
const homepage = computed(() => mainStore.homepage);
const isThemePreview = computed(() => mainStore.isThemePreview);

const id = computed(() => `${props.containerId}_${props.componentIdx}`);

const themeOptions = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...theme.value.containers[props.containerIdx].components[props.componentIdx].options,
    translations: translationStore.getComponentTranslation('custom_component'),
  };
});

const content = computed(() =>
  isThemePreview.value ? homepage.value.custom_component.preview_id : homepage.value.custom_component[id.value],
);

function sanitizeName({ inLoop, type, name, loopIndex }) {
  if (!isThemePreview.value) return name;
  if (inLoop && loopIndex === undefined) return 'cms-loop';
  return `cms-${type}`;
}

function getExistingFields({ inLoop, loopName, loopIndex }) {
  if (!inLoop) return content.value;
  const loopedContent = isThemePreview.value
    ? content.value.loop['cms-loop'][0]
    : content.value.loop[loopName]?.[loopIndex];
  return loopedContent;
}

function getCustomValue(inLoop, loopName, loopIndex, type, name, field) {
  const paramsObj = { inLoop, loopName, loopIndex, type, name, field };
  const sanitizedName = sanitizeName(paramsObj);
  const existingFields = getExistingFields(paramsObj);
  return get(existingFields, `${type}.${sanitizedName}.${field}`, '');
}
</script>
